/**
 * An answer, rendered — paragraphs, lists, tables and charts, with `[n]` markers still clickable.
 *
 * ## Why tables
 *
 * The design shows a fee comparison as a table inside the answer, and that is the right call for
 * the material this assistant handles: a tariff with three channels and two amounts is four lines
 * of prose an employee has to hold in their head, or a grid they can read across. The system
 * prompts now ask for one where it fits, so the renderer has to be able to draw it.
 *
 * ## Why a hand-written renderer rather than a markdown library
 *
 * The same argument the icons and the router make. What is needed is four block types — paragraph,
 * bullet list, ordered list, pipe table — over text this platform's own prompts produce. A
 * markdown package brings a parser, an AST, a sanitiser and a plugin system, and every one of them
 * is surface a bank's review has to cover for a feature that is four `if` branches. It stops being
 * the right call the moment answers need images, links or nested structure; `docs/adr/0006` makes
 * the same note about the router, and this paragraph is where whoever hits that wall should look.
 *
 * ## Why charts are a declared block rather than something inferred
 *
 * The alternative was reading the numbers back out of a table the model wrote and guessing which
 * column is a series and which is a label. That guess is wrong on the first table carrying a
 * footnote or a currency symbol, and when it is wrong it draws a chart of the wrong figures —
 * a failure that looks authoritative. So the model declares the shape in a ```nab-chart``` fence
 * and `chartSpec.ts` refuses anything it did not declare cleanly. The prompts tell it exactly what
 * that fence may contain.
 *
 * A fence that does not validate renders one muted line rather than a wall of JSON, and rather
 * than nothing: the prompts require a chart to restate figures that are already in the prose or
 * the table beside it, so the answer is complete without it — but an employee should still be able
 * to see that something was meant to be there.
 *
 * **Nothing here renders HTML.** The model's output is text and is inserted as text — there is no
 * `dangerouslySetInnerHTML` in this file, which is the property that makes rendering model output
 * safe at all.
 */

import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { AnswerChart } from "./AnswerChart";
import { parseChartSpec, type ChartSpec } from "./chartSpec";
import { CitationMarkers } from "./CitationMarkers";
import { Emphasis } from "./Emphasis";

import type { Citation, Direction } from "@nab/shared";

export interface AnswerBodyProps {
  text: string;
  citations: Citation[];
  onCitationClick: (index: number) => void;
  /** The message's own direction, which a chart's category axis follows. */
  direction: Direction;
}

type Block =
  | { kind: "paragraph"; lines: string[] }
  | { kind: "bullets"; items: string[]; ordered: boolean }
  | { kind: "table"; head: string[]; rows: string[][] }
  | { kind: "chart"; spec: ChartSpec }
  /** A `nab-chart` fence that did not validate. See the module docstring. */
  | { kind: "chart-error" };

/** A `| a | b |` row. */
const TABLE_ROW = /^\s*\|(.+)\|\s*$/;
/** The `|---|:--:|` separator under a table's header. */
const TABLE_RULE = /^\s*\|[\s:|-]+\|\s*$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;
/** The opening of a chart fence: ```nab-chart. */
const CHART_OPEN = /^\s*```+\s*nab-chart\s*$/i;
/** Any closing fence. */
const FENCE_CLOSE = /^\s*```+\s*$/;

function cells(line: string): string[] {
  const inner = TABLE_ROW.exec(line)?.[1] ?? "";
  return inner.split("|").map((cell) => cell.trim());
}

/**
 * Split an answer into blocks.
 *
 * Deliberately forgiving: a model that writes a table without the `|---|` separator still gets a
 * table, and anything unrecognised stays a paragraph. The failure that matters is a fee grid
 * rendered as a wall of pipes, and being strict is how that happens.
 */
export function parseBlocks(text: string): Block[] {
  const lines = text.split("\n");
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flush = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", lines: paragraph });
      paragraph = [];
    }
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]!;

    if (line.trim() === "") {
      flush();
      continue;
    }

    if (CHART_OPEN.test(line)) {
      flush();
      let cursor = index + 1;
      const body: string[] = [];
      while (cursor < lines.length && !FENCE_CLOSE.test(lines[cursor]!)) {
        body.push(lines[cursor]!);
        cursor += 1;
      }
      // An unterminated fence is a truncated answer, and `_fit_to_context` makes those possible.
      // Consuming to the end is right either way: whatever follows an unclosed fence is JSON, not
      // prose, and showing it would be worse than the one muted line.
      const spec = cursor < lines.length ? parseChartSpec(body.join("\n")) : null;
      blocks.push(spec ? { kind: "chart", spec } : { kind: "chart-error" });
      index = cursor;
      continue;
    }

    if (TABLE_ROW.test(line) && !TABLE_RULE.test(line)) {
      const head = cells(line);
      let cursor = index + 1;
      if (cursor < lines.length && TABLE_RULE.test(lines[cursor]!)) cursor += 1;

      const rows: string[][] = [];
      while (
        cursor < lines.length &&
        TABLE_ROW.test(lines[cursor]!) &&
        !TABLE_RULE.test(lines[cursor]!)
      ) {
        rows.push(cells(lines[cursor]!));
        cursor += 1;
      }

      // One pipe line on its own is a sentence that happens to contain a pipe, not a table.
      if (rows.length > 0) {
        flush();
        blocks.push({ kind: "table", head, rows });
        index = cursor - 1;
        continue;
      }
    }

    const bullet = BULLET.exec(line);
    const ordered = ORDERED.exec(line);
    if (bullet || ordered) {
      flush();
      const isOrdered = Boolean(ordered);
      const items: string[] = [(bullet ?? ordered)![1]!];
      let cursor = index + 1;
      // A blank line between items does not end the list.
      //
      // Models put one there routinely, and treating it as a terminator started a *new* `<ol>` at
      // every item — so a five-step procedure rendered as five lists each numbered "1.", which is
      // worse than no numbering because an employee reads it as five alternatives. `consumed`
      // tracks the last line that was really part of the list, so a blank line followed by prose
      // still ends it and the blank is not swallowed.
      let consumed = index;
      while (cursor < lines.length) {
        if (lines[cursor]!.trim() === "") {
          cursor += 1;
          continue;
        }
        const next = isOrdered ? ORDERED.exec(lines[cursor]!) : BULLET.exec(lines[cursor]!);
        if (!next) break;
        items.push(next[1]!);
        consumed = cursor;
        cursor += 1;
      }
      blocks.push({ kind: "bullets", items, ordered: isOrdered });
      index = consumed;
      continue;
    }

    paragraph.push(line);
  }

  flush();
  return blocks;
}

export function AnswerBody({
  text,
  citations,
  onCitationClick,
  direction,
}: AnswerBodyProps): JSX.Element {
  const { t } = useTranslation("app");
  const blocks = parseBlocks(text);

  const inline = (value: string): JSX.Element => (
    <Emphasis text={value}>
      {(run) => (
        <CitationMarkers
          text={run}
          citations={citations}
          onCitationClick={onCitationClick}
        />
      )}
    </Emphasis>
  );

  return (
    <>
      {blocks.map((block, index) => {
        if (block.kind === "chart") {
          return (
            <AnswerChart
              key={index}
              spec={block.spec}
              citations={citations}
              onCitationClick={onCitationClick}
              direction={direction}
            />
          );
        }

        if (block.kind === "chart-error") {
          return (
            <p key={index} className="mb-4 text-label-md text-textMuted">
              {t("chart.unavailable")}
            </p>
          );
        }

        if (block.kind === "table") {
          return (
            // `overflow-x-auto` on its own container, as the design has it: a fee table with four
            // columns does not fit a phone, and the answer around it must not scroll sideways.
            <div
              key={index}
              className="mb-4 overflow-x-auto rounded-[16px] border border-borderSubtle bg-surfaceRaised shadow-sm"
            >
              <table className="w-full min-w-[400px] border-collapse text-start">
                <thead className="bg-surfaceRaised text-label-md font-medium tracking-wide text-textMuted">
                  <tr>
                    {block.head.map((cell, column) => (
                      <th
                        key={column}
                        scope="col"
                        className="border-b border-borderStrong px-4 py-3 text-start"
                      >
                        <bdi>{cell}</bdi>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-body-sm">
                  {block.rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-surfaceMuted transition-colors hover:bg-primarySubtle"
                    >
                      {row.map((cell, column) => (
                        <td
                          key={column}
                          className={
                            column === 0
                              ? "whitespace-nowrap px-4 py-3 font-medium text-textPrimary"
                              : "px-4 py-3 text-textPrimary"
                          }
                        >
                          <bdi>{inline(cell)}</bdi>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        if (block.kind === "bullets") {
          const List = block.ordered ? "ol" : "ul";
          return (
            <List
              key={index}
              className={
                block.ordered
                  ? "mb-4 list-decimal space-y-1 ps-5 leading-relaxed"
                  : "mb-4 list-disc space-y-1 ps-5 leading-relaxed"
              }
            >
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{inline(item)}</li>
              ))}
            </List>
          );
        }

        return (
          <p key={index} className="mb-3 leading-relaxed md:mb-4">
            {block.lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 ? <br /> : null}
                {inline(line)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}
